/**
 * ContactRail — the right column of ContactDetail, split into tabs so the
 * growing toolset never buries the conversation:
 *   Next steps — callbacks, tasks, send-a-form (the work surface)
 *   Screening  — prescreen runner + attempt history (the live screen)
 *   Profile    — completeness, meds, diagnoses, ratings, screener answers,
 *                participation, enrollments (who the patient is — Phase G)
 *
 * A purple dot on the Screening tab nudges recruiters when the contact has a
 * lead-linked study but no prescreen attempt yet. The Profile tab label
 * carries the completeness meter ("Profile · 60%").
 */
import { useMemo, useState } from 'react';
import { ContactDetailResponse, Enrollment, ScheduleReadiness, ScheduleReadinessItem } from '../types';
import CallbacksCard from './CallbacksCard';
import TasksCard from './TasksCard';
import SendFormCard from './SendFormCard';
import RecordsCard from './records/RecordsCard';
import ScheduleReadinessCard from './ScheduleReadinessCard';
import ScreeningTab from './prescreen/ScreeningTab';
import ProfileTab from './profile/ProfileTab';

type RailTab = 'next' | 'screening' | 'profile';

interface ContactRailProps {
  detail: ContactDetailResponse;
  enrollments: Enrollment[];
  reloadToken: number;
  /** Debounced Realtime ping (ContactDetail) — silent rail-card refetches. */
  pingTick?: number;
  onSent: () => void;
  onChanged: () => void;
  isSuperAdmin: boolean;
  reenrolling: boolean;
  onReEnroll: () => void;
  /** Attempt count is unknown until ScreeningTab loads; dot shows pre-attempt nudge. */
  hasRunnableStudy: boolean;
  /** Drop a rendered SMS draft into the docked composer (booking-link quick action). */
  prefillComposer?: (text: string) => void;
  /** Scheduling readiness for the lead's current study (owned by ContactDetail). */
  scheduleReadiness?: ScheduleReadiness | null;
  /** Tick a manual scheduling gate (CRC / investigator / custom sign-off). */
  onToggleGate: (item: ScheduleReadinessItem, done: boolean) => Promise<void>;
}

export default function ContactRail({
  detail, enrollments, reloadToken, pingTick = 0, onSent, onChanged,
  isSuperAdmin, reenrolling, onReEnroll, hasRunnableStudy, prefillComposer,
  scheduleReadiness, onToggleGate,
}: ContactRailProps) {
  const [tab, setTab] = useState<RailTab>('next');
  const c = detail.contact;

  const contactStudySlugs = useMemo(
    () => [...new Set((detail.leads || []).map((l) => l.study_slug).filter(Boolean))] as string[],
    [detail.leads]
  );

  const profileLabel = detail.profile_completeness
    ? `Profile · ${detail.profile_completeness.percent}%`
    : 'Profile';

  const tabBtn = (key: RailTab, label: string, dot?: boolean) => (
    <button
      type="button"
      onClick={() => setTab(key)}
      className={`relative flex-1 rounded-lg px-1.5 py-2 text-sm font-medium transition-colors duration-150 min-h-[44px] sm:min-h-0 sm:px-2 ${
        tab === key
          ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
          : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
      }`}
    >
      {label}
      {dot && tab !== key && (
        <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
      )}
    </button>
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Segmented control sticks under the top bar as the whole page scrolls,
          so switching tabs never requires scrolling back up — even on a long
          Prescreening or Profile tab. */}
      <div className="sticky top-14 z-10 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-900/60">
        {tabBtn('next', 'Next steps')}
        {tabBtn('screening', 'Prescreening', hasRunnableStudy)}
        {tabBtn('profile', profileLabel)}
      </div>

      {/* The rail content grows to its full height and scrolls with the whole
          page on every breakpoint — no inner scroll box — so the cards sit on
          the page rather than being crammed into a short window. */}
      <div className="space-y-4">
        {tab === 'next' && (
          <div className="space-y-4">
            <ScheduleReadinessCard readiness={scheduleReadiness ?? null} onToggle={onToggleGate} />
            <SendFormCard contact={c} reloadToken={reloadToken} onSent={onSent} />
            <TasksCard contactId={c.id} />
            <RecordsCard contact={c} reloadToken={reloadToken} pingTick={pingTick} onSent={onSent} />
            <CallbacksCard
              contact={c}
              maxAttempts={detail.max_attempts || 6}
              reloadToken={reloadToken}
              onSent={onSent}
              prefillComposer={prefillComposer}
            />
          </div>
        )}

        {tab === 'screening' && (
          <ScreeningTab
            contactId={c.id}
            contactStudySlugs={contactStudySlugs}
            reloadToken={reloadToken}
            onChanged={onChanged}
          />
        )}

        {tab === 'profile' && (
          <ProfileTab
            detail={detail}
            enrollments={enrollments}
            reloadToken={reloadToken}
            pingTick={pingTick}
            isSuperAdmin={isSuperAdmin}
            reenrolling={reenrolling}
            onReEnroll={onReEnroll}
          />
        )}
      </div>
    </div>
  );
}
